import React, { useState, useEffect } from "react";
import { MdMenu, MdClose } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import ThemeIcon from "../common/ThemeIcon";
import NavBar from "./NavBar";
import { navItems } from "../../config/navigationConfig";
import { useTheme } from "../../hooks/useTheme";
import { useScrollEffects } from "../../hooks/useScrollEffects";

export default function Header() {
    const { theme, menuOpen, setMenuOpen, handleChange, themeTranslations } = useTheme();
    const { isScrolled, activeSection, handleNavClick } = useScrollEffects();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setMobileMenuOpen(false);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [mobileMenuOpen]);

    const handleMobileNavClick = (e, id) => {
        handleNavClick(e, id);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <motion.header
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    isScrolled
                        ? "bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl shadow-lg border-b border-gray-200/20 dark:border-gray-700/20"
                        : "bg-transparent"
                }`}
            >
                <div className="max-w-7xl mx-auto px-4 lg:px-6">
                    <div className="flex items-center justify-between h-14">
                        {/* Logo/Brand */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2, duration: 0.5 }}
                            className="flex-shrink-0"
                        >
                            <button onClick={(e) => handleNavClick(e, "inicio")} className="group relative text-xl font-bold">
                                <span className="relative z-10 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                                    Portfolio
                                </span>
                                <motion.div
                                    className="absolute -inset-2 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 rounded-lg blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                    initial={false}
                                />
                            </button>
                        </motion.div>

                        {/* Desktop Navigation */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.5 }}
                            className="hidden lg:flex items-center gap-1"
                        >
                            {/* NavBar Component */}
                            <NavBar 
                                variant="header"
                                activeSection={activeSection}
                                onItemClick={handleNavClick}
                                animateOnMount={true}
                                animationDelay={0.4}
                            />

                            {/* Divider */}
                            <div className="w-px h-6 bg-gray-300 dark:bg-gray-700 mx-1" />

                            {/* Theme Toggle */}
                            <div className="relative" id="theme-toggle-container">
                                <motion.button
                                    id="theme-toggle-btn"
                                    onClick={() => setMenuOpen(!menuOpen)}
                                    className="relative p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300 group"
                                    whileHover={{ scale: 1.05, rotate: 12 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <ThemeIcon selected={theme} />
                                    <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-300"
                                        initial={false}
                                    />
                                </motion.button>

                                <AnimatePresence>
                                    {menuOpen && (
                                        <motion.div
                                            id="theme-menu"
                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                            transition={{ duration: 0.2, ease: "easeOut" }}
                                            className="absolute top-full mt-2 right-0 w-40 rounded-xl border border-gray-200/50 dark:border-gray-700/50 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl shadow-2xl overflow-hidden"
                                        >
                                            <div className="p-1.5">
                                                {["light", "dark", "system"].map((option, index) => (
                                                    <motion.button
                                                        key={option}
                                                        onClick={() => handleChange(option)}
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: index * 0.05 }}
                                                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 ${
                                                            theme === option
                                                                ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                                                                : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                                        }`}
                                                        whileHover={{ x: 4 }}
                                                        whileTap={{ scale: 0.98 }}
                                                    >
                                                        <span className="font-medium text-xs">{themeTranslations[option]}</span>
                                                        <ThemeIcon
                                                            selected={option}
                                                            isInMenu={true}
                                                            isActive={theme === option}
                                                            currentTheme={theme}
                                                        />
                                                    </motion.button>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>

                        {/* Mobile Menu Button */}
                        <div className="lg:hidden flex items-center gap-2">
                            <motion.button
                                id="theme-toggle-btn-mobile"
                                onClick={() => setMenuOpen(!menuOpen)}
                                className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <ThemeIcon selected={theme} />
                            </motion.button>

                            <motion.button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <AnimatePresence mode="wait">
                                    {mobileMenuOpen ? (
                                        <motion.div
                                            key="close"
                                            initial={{ rotate: -90, opacity: 0 }}
                                            animate={{ rotate: 0, opacity: 1 }}
                                            exit={{ rotate: 90, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <MdClose className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key="menu"
                                            initial={{ rotate: 90, opacity: 0 }}
                                            animate={{ rotate: 0, opacity: 1 }}
                                            exit={{ rotate: -90, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <MdMenu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.button>
                        </div>
                    </div>
                </div>

                {/* Theme Menu Mobile */}
                <AnimatePresence>
                    {menuOpen && (
                        <motion.div
                            id="theme-menu-mobile"
                            initial={{ opacity: 0, y: -10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className="lg:hidden absolute top-full mt-2 right-4 w-40 rounded-xl border border-gray-200/50 dark:border-gray-700/50 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl shadow-2xl overflow-hidden"
                        >
                            <div className="p-1.5">
                                {["light", "dark", "system"].map((option, index) => (
                                    <motion.button
                                        key={option}
                                        onClick={() => handleChange(option)}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 ${
                                            theme === option
                                                ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                                                : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                        }`}
                                        whileHover={{ x: 4 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <span className="font-medium text-xs">{themeTranslations[option]}</span>
                                        <ThemeIcon selected={option} isInMenu={true} isActive={theme === option} currentTheme={theme} />
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.header>

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
                            onClick={() => setMobileMenuOpen(false)}
                        />

                        {/* Menu Panel */}
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", damping: 25, stiffness: 250 }}
                            className="fixed top-14 right-0 bottom-0 z-40 w-80 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-2xl lg:hidden overflow-y-auto border-l border-gray-200/50 dark:border-gray-700/50"
                        >
                            <nav className="p-6">
                                <ul className="space-y-2">
                                    {navItems.map((item, index) => {
                                        const IconComponent = item.icon;
                                        const isActive = activeSection === item.id;

                                        return (
                                            <motion.li
                                                key={item.id}
                                                initial={{ opacity: 0, x: 20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: index * 0.05, duration: 0.3 }}
                                            >
                                                <button
                                                    onClick={(e) => handleMobileNavClick(e, item.id)}
                                                    className={`w-full group relative flex items-center gap-4 px-5 py-4 rounded-2xl transition-all duration-300 overflow-hidden ${
                                                        isActive
                                                            ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/25"
                                                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                                    }`}
                                                >
                                                    {/* Background effect */}
                                                    {!isActive && (
                                                        <motion.div
                                                            className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                                            initial={false}
                                                        />
                                                    )}

                                                    <IconComponent
                                                        className={`relative z-10 w-5 h-5 transition-transform duration-300 ${
                                                            isActive ? "scale-110" : "group-hover:scale-110"
                                                        }`}
                                                    />
                                                    <span className="relative z-10 font-semibold">{item.label}</span>

                                                    {isActive && (
                                                        <motion.div
                                                            className="absolute right-4 w-2 h-2 bg-white rounded-full"
                                                            layoutId="activeMobileDot"
                                                            transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                                        />
                                                    )}
                                                </button>
                                            </motion.li>
                                        );
                                    })}
                                </ul>
                            </nav>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
